import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, ToggleButton, ToggleButtonGroup,
  TextField, MenuItem, Button, Paper, Collapse, Link as MuiLink,
} from '@mui/material';
import AUHeader from '../components/AUHeader';
import VideoAccessForm from '../components/VideoAccessForm';
import {
  ACTIVITY_LEVELS, PROTEIN_LEVELS, computeResults, parseDecimal,
  DEFICIT_PCT, FAT_G_PER_KG_MAINTENANCE, FAT_G_PER_KG_DEFICIT, LOW_CARB_THRESHOLD_G,
} from '../utils/calorieMacro';
import { hasValidVideoAccess, setStoredVideoAccess } from '../utils/videoAccessStorage';
import { submitLead } from '../utils/submitLead';

const BLUE = '#1A7FFF';
const BLUE_HOVER = '#1568D9';
const BLUE_SOFT = '#EBF3FF';
const DARK = '#0F1117';
const GRAY = '#64748B';
const BORDER = '#E2E8F0';
const GRAY_LIGHT = '#F8FAFC';

const MACRO_META = {
  protein: { label: 'Proteína', color: BLUE },
  carbs: { label: 'Carbohidratos', color: DARK },
  fat: { label: 'Grasa', color: '#94A3B8' },
};
const MACRO_ORDER = ['protein', 'carbs', 'fat'];

const round = (n) => Math.round(n);

function MacroBar({ macros }) {
  return (
    <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', mb: 2, bgcolor: BORDER }}>
      {MACRO_ORDER.filter((k) => macros[k].pct > 0).map((k) => (
        <Box key={k} sx={{ width: `${macros[k].pct}%`, bgcolor: MACRO_META[k].color }} />
      ))}
    </Box>
  );
}

function MacroRow({ macroKey, macro }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: MACRO_META[macroKey].color }} />
        <Typography sx={{ fontWeight: 600, color: DARK, fontFamily: 'Kanit, sans-serif' }}>
          {MACRO_META[macroKey].label}
        </Typography>
      </Box>
      <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 14 }}>
        <Box component="span" sx={{ fontWeight: 700, color: DARK }}>{round(macro.grams)} g</Box>
        {` · ${round(macro.kcal)} kcal · ${round(macro.pct)}%`}
      </Typography>
    </Box>
  );
}

function ResultCard({ title, subtitle, kcal, macros, highlight }) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1, p: 3, borderRadius: 3,
        border: `1px solid ${highlight ? BLUE : BORDER}`,
        bgcolor: highlight ? BLUE_SOFT : '#fff',
      }}
    >
      <Typography sx={{
        fontSize: 13, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
        color: highlight ? BLUE : GRAY, fontFamily: 'Kanit, sans-serif',
      }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 13, color: GRAY, mb: 1, fontFamily: 'Kanit, sans-serif' }}>
          {subtitle}
        </Typography>
      )}
      <Typography sx={{ fontWeight: 800, color: DARK, lineHeight: 1.1, mb: 2, fontFamily: 'Kanit, sans-serif', fontSize: '2.5rem' }}>
        {round(kcal)}{' '}
        <Box component="span" sx={{ fontSize: '1rem', fontWeight: 600, color: GRAY }}>kcal/día</Box>
      </Typography>
      <MacroBar macros={macros} />
      {MACRO_ORDER.map((k) => <MacroRow key={k} macroKey={k} macro={macros[k]} />)}
    </Paper>
  );
}

const fieldSx = { fontFamily: 'Kanit, sans-serif' };

const toggleSx = {
  fontFamily: 'Kanit, sans-serif',
  textTransform: 'none',
  fontWeight: 600,
  color: GRAY,
  '&.Mui-selected': {
    bgcolor: BLUE,
    color: '#fff',
    '&:hover': { bgcolor: BLUE_HOVER },
  },
};

const CalorieMacroCalculatorPage = () => {
  const navigate = useNavigate();
  const [sex, setSex] = useState('male');
  const [age, setAge] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [activityId, setActivityId] = useState('moderado');
  const [proteinId, setProteinId] = useState('p22');
  const [result, setResult] = useState(null);
  const [showHow, setShowHow] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);

  const activity = ACTIVITY_LEVELS.find((l) => l.id === activityId);
  const proteinLevel = PROTEIN_LEVELS.find((p) => p.id === proteinId);

  const ageNum = parseDecimal(age);
  const heightNum = parseDecimal(height);
  const weightNum = parseDecimal(weight);

  const isValid =
    (sex === 'male' || sex === 'female') &&
    Number.isFinite(ageNum) && ageNum >= 15 && ageNum <= 90 &&
    Number.isFinite(heightNum) && heightNum > 0 &&
    Number.isFinite(weightNum) && weightNum > 0 &&
    !!activityId;

  const runCalculation = () => {
    setResult(computeResults({
      sex, age: ageNum, heightCm: heightNum, weightKg: weightNum,
      activityFactor: activity.factor, proteinGperKg: proteinLevel.value,
    }));
  };

  const handleCalculate = () => {
    if (!isValid) return;
    // Captura de lead (mismo mecanismo que el desbloqueo del video).
    // Si ya dejó sus datos antes, calcula directo sin volver a pedirlos.
    if (hasValidVideoAccess()) {
      runCalculation();
    } else {
      setShowLeadForm(true);
    }
  };

  const handleLeadSubmit = async (data) => {
    setShowLeadForm(false);
    setStoredVideoAccess(data);
    await submitLead({ data, source: 'calorie-macro-calculator' });
    runCalculation();
  };

  const lowCarb = result && result.deficit.macros.carbs.grams < LOW_CARB_THRESHOLD_G;

  return (
    <Box sx={{ bgcolor: GRAY_LIGHT, fontFamily: 'Kanit, sans-serif', minHeight: '100vh' }}>
      <AUHeader />

      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
        <Typography sx={{
          fontSize: 15, fontWeight: 700, letterSpacing: 2.5, color: BLUE,
          textTransform: 'uppercase', mb: 0.75, fontFamily: 'Kanit, sans-serif',
        }}>
          Herramienta gratis
        </Typography>
        <Typography component="h1" sx={{
          fontFamily: 'Kanit, sans-serif', fontWeight: 800,
          fontSize: { xs: '2rem', md: '2.8rem' }, color: DARK, mb: 1,
        }}>
          Calculadora de calorías y macros
        </Typography>
        <Typography sx={{ color: GRAY, mb: 4, maxWidth: 640, fontFamily: 'Kanit, sans-serif' }}>
          Calcula tus calorías de mantenimiento, tu objetivo en déficit y los macros para perder grasa sin perder músculo.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
          {/* Formulario */}
          <Paper elevation={0} sx={{ flex: '0 0 auto', width: { xs: '100%', md: 360 }, p: 3, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
            <Typography sx={{ fontWeight: 700, color: DARK, mb: 2, fontFamily: 'Kanit, sans-serif' }}>
              Tus datos
            </Typography>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: GRAY, mb: 1, fontFamily: 'Kanit, sans-serif' }}>
              Sexo
            </Typography>
            <ToggleButtonGroup
              exclusive fullWidth value={sex} onChange={(e, v) => v && setSex(v)} sx={{ mb: 2 }}
            >
              <ToggleButton value="male" sx={toggleSx}>Hombre</ToggleButton>
              <ToggleButton value="female" sx={toggleSx}>Mujer</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label="Edad" fullWidth value={age}
              onChange={(e) => setAge(e.target.value)}
              inputProps={{ inputMode: 'numeric' }}
              sx={{ mb: 2 }} InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: fieldSx }}
            />
            <TextField
              label="Altura (cm)" fullWidth value={height}
              onChange={(e) => setHeight(e.target.value)}
              inputProps={{ inputMode: 'numeric' }}
              sx={{ mb: 2 }} InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: fieldSx }}
            />
            <TextField
              label="Peso (kg)" fullWidth value={weight}
              onChange={(e) => setWeight(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              sx={{ mb: 2 }} InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: fieldSx }}
            />
            <TextField
              select label="Nivel de actividad" fullWidth value={activityId}
              onChange={(e) => setActivityId(e.target.value)}
              sx={{ mb: 1.5 }} InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: fieldSx }}
              SelectProps={{ renderValue: (val) => ACTIVITY_LEVELS.find((l) => l.id === val)?.label }}
            >
              {ACTIVITY_LEVELS.map((l) => (
                <MenuItem key={l.id} value={l.id} sx={{ ...fieldSx, display: 'block', py: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14 }}>
                    {l.label}
                  </Typography>
                  <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 12, whiteSpace: 'normal' }}>
                    {`${l.trainings} · ${l.daily}`}
                  </Typography>
                </MenuItem>
              ))}
            </TextField>

            {activity && (
              <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: BLUE_SOFT, border: `1px solid ${BORDER}` }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: BLUE, fontFamily: 'Kanit, sans-serif', mb: 0.75 }}>
                  Tu nivel: {activity.label}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: DARK, fontFamily: 'Kanit, sans-serif', minWidth: 92 }}>
                    Entrenamiento
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: GRAY, fontFamily: 'Kanit, sans-serif' }}>
                    {activity.trainings}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: DARK, fontFamily: 'Kanit, sans-serif', minWidth: 92 }}>
                    Día a día
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: GRAY, fontFamily: 'Kanit, sans-serif' }}>
                    {activity.daily}
                  </Typography>
                </Box>
              </Paper>
            )}

            <Typography sx={{ fontSize: 12, color: GRAY, mb: 2, fontFamily: 'Kanit, sans-serif' }}>
              "Muy alto" no se alcanza solo con pasos: requiere trabajo físico pesado o doble sesión. Si dudas entre dos niveles, elige el menor.
            </Typography>

            <TextField
              select label="Nivel de proteína" fullWidth value={proteinId}
              onChange={(e) => setProteinId(e.target.value)}
              sx={{ mb: 1.5 }} InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: fieldSx }}
              SelectProps={{ renderValue: (val) => PROTEIN_LEVELS.find((p) => p.id === val)?.label }}
            >
              {PROTEIN_LEVELS.map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ ...fieldSx, display: 'block', py: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: DARK, fontFamily: 'Kanit, sans-serif', fontSize: 14 }}>
                    {p.label}
                  </Typography>
                  <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif', fontSize: 12, whiteSpace: 'normal' }}>
                    {p.desc}
                  </Typography>
                </MenuItem>
              ))}
            </TextField>
            <Typography sx={{ fontSize: 12, color: GRAY, mb: 2, fontFamily: 'Kanit, sans-serif' }}>
              Mientras más músculo tengas que preservar y más magro estés, más cerca de 2.5 g/kg. Todo este rango ya es proteína alta.
            </Typography>

            <Button
              fullWidth variant="contained" disableElevation
              disabled={!isValid} onClick={handleCalculate}
              sx={{
                py: 1.4, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 16,
                textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
              }}
            >
              Calcular
            </Button>
          </Paper>

          {/* Resultados */}
          <Box sx={{ flex: 1, width: '100%' }}>
            {!result && (
              <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: `1px dashed ${BORDER}`, bgcolor: '#fff', textAlign: 'center' }}>
                <Typography sx={{ color: GRAY, fontFamily: 'Kanit, sans-serif' }}>
                  Completa tus datos y presiona <b>Calcular</b> para ver tus calorías y macros.
                </Typography>
              </Paper>
            )}

            {result && (
              <>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 2 }}>
                  <ResultCard title="Mantenimiento" kcal={result.maintenance.kcal} macros={result.maintenance.macros} />
                  <ResultCard title="Déficit · pérdida de grasa" subtitle={`-${Math.round(DEFICIT_PCT * 100)}% de tu mantenimiento`} kcal={result.deficit.kcal} macros={result.deficit.macros} highlight />
                </Box>

                {lowCarb && (
                  <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: GRAY_LIGHT }}>
                    <Typography sx={{ fontSize: 14, color: DARK, fontFamily: 'Kanit, sans-serif' }}>
                      Con tu peso, estos valores de proteína y grasa dejan pocos carbohidratos. Un coach ajustaría esto a tu caso real.
                    </Typography>
                  </Paper>
                )}

                <Typography sx={{ fontSize: 13, color: GRAY, mb: 2, fontFamily: 'Kanit, sans-serif' }}>
                  Esto es un punto de partida estimado. Ajusta según cómo evolucione tu peso en 2-3 semanas.
                </Typography>

                <MuiLink
                  component="button" type="button" underline="hover"
                  onClick={() => setShowHow((s) => !s)}
                  sx={{ color: BLUE, fontFamily: 'Kanit, sans-serif', fontWeight: 600, fontSize: 14, mb: 1 }}
                >
                  {showHow ? 'Ocultar cómo se calcula' : 'Cómo se calcula'}
                </MuiLink>
                <Collapse in={showHow}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
                    <Typography component="div" sx={{ fontSize: 14, color: DARK, fontFamily: 'Kanit, sans-serif' }}>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li><b>Calorías base (BMR):</b> fórmula Mifflin-St Jeor con tu sexo, edad, altura y peso.</li>
                        <li><b>Mantenimiento:</b> BMR multiplicado por tu factor de actividad.</li>
                        <li><b>Déficit:</b> {Math.round(DEFICIT_PCT * 100)}% menos que tu mantenimiento.</li>
                        <li><b>Proteína:</b> {proteinLevel.value} g por kilo de peso (la elegiste según tu masa muscular y experiencia).</li>
                        <li><b>Grasa:</b> {FAT_G_PER_KG_MAINTENANCE} g por kilo en mantenimiento y {FAT_G_PER_KG_DEFICIT} g por kilo en déficit (en déficit se baja la grasa antes que los carbos).</li>
                        <li><b>Carbohidratos:</b> las calorías que quedan después de proteína y grasa.</li>
                      </ul>
                    </Typography>
                  </Paper>
                </Collapse>

                {/* CTA suave a coaching */}
                <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: DARK }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontFamily: 'Kanit, sans-serif', fontSize: 18, mb: 0.5 }}>
                    ¿Quieres que esto lo armen por ti y te hagan seguimiento?
                  </Typography>
                  <Typography sx={{ color: '#CBD5E1', fontFamily: 'Kanit, sans-serif', fontSize: 14, mb: 2 }}>
                    Un número es solo el punto de partida. En AU Coaching lo ajustamos a tus datos reales semana a semana.
                  </Typography>
                  <Button
                    variant="contained" disableElevation onClick={() => navigate('/form')}
                    sx={{
                      py: 1.2, px: 3, fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: 15,
                      textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: BLUE_HOVER },
                    }}
                  >
                    Agenda con AU Coaching
                  </Button>
                </Paper>
              </>
            )}
          </Box>
        </Box>
      </Container>

      <VideoAccessForm
        open={showLeadForm}
        onClose={() => setShowLeadForm(false)}
        onSubmit={handleLeadSubmit}
        title="Recibe tus macros"
        subtitle="Déjanos tus datos y te mostramos tus calorías y macros al instante."
        submitLabel="Ver mis macros"
      />
    </Box>
  );
};

export default CalorieMacroCalculatorPage;
